/**
 * Speech Service
 * Voice-to-text using Web Speech API
 * With mobile browser auto-restart support
 */

class SpeechService {
    constructor() {
        this.recognition = null;
        this.isListening = false;
        this.shouldContinue = false; // Flag to control auto-restart on mobile
        this.transcript = '';
        this.onResult = null;
        this.onEnd = null;
        this.onError = null;
        this.restartTimeout = null;
    }

    /**
     * Check if speech recognition is supported
     */
    isSupported() {
        return 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;
    }

    /**
     * Detect if running on mobile
     */
    isMobile() {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    }

    /**
     * Initialize speech recognition
     */
    init() {
        if (!this.isSupported()) {
            console.error('Speech recognition not supported');
            return false;
        }

        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        this.recognition = new SpeechRecognition();

        // Configuration
        this.recognition.continuous = true;
        this.recognition.interimResults = true;
        this.recognition.lang = 'es-ES';

        // Event handlers
        this.recognition.onresult = (event) => {
            let interimTranscript = '';
            let finalTranscript = '';

            for (let i = event.resultIndex; i < event.results.length; i++) {
                const transcript = event.results[i][0].transcript;
                if (event.results[i].isFinal) {
                    finalTranscript += transcript + ' ';
                } else {
                    interimTranscript += transcript;
                }
            }

            if (finalTranscript) {
                this.transcript += finalTranscript;
            }

            if (this.onResult) {
                this.onResult({
                    final: this.transcript,
                    interim: interimTranscript,
                    full: this.transcript + interimTranscript
                });
            }
        };

        this.recognition.onend = () => {
            // On mobile, the recognition stops frequently even with continuous=true
            // Auto-restart if we should still be listening
            if (this.shouldContinue && this.isListening) {
                console.log('Recognition ended, auto-restarting for mobile...');
                // Small delay to prevent rapid restart loops
                this.restartTimeout = setTimeout(() => {
                    if (this.shouldContinue) {
                        try {
                            this.recognition.start();
                        } catch (e) {
                            console.error('Failed to restart recognition:', e);
                            this.isListening = false;
                            this.shouldContinue = false;
                            if (this.onEnd) {
                                this.onEnd(this.cleanTranscript(this.transcript));
                            }
                        }
                    }
                }, 100);
                return;
            }

            this.isListening = false;
            if (this.onEnd) {
                this.onEnd(this.cleanTranscript(this.transcript));
            }
        };

        this.recognition.onerror = (event) => {
            console.error('Speech recognition error:', event.error);

            // On mobile, 'no-speech' error is common - auto-restart
            if (event.error === 'no-speech' && this.shouldContinue) {
                console.log('No speech detected, restarting...');
                this.restartTimeout = setTimeout(() => {
                    if (this.shouldContinue) {
                        try {
                            this.recognition.start();
                        } catch (e) {
                            console.error('Failed to restart after no-speech:', e);
                        }
                    }
                }, 100);
                return;
            }

            // 'aborted' error is normal when user stops manually
            if (event.error === 'aborted') {
                return;
            }

            this.isListening = false;
            this.shouldContinue = false;
            if (this.onError) {
                this.onError(event.error);
            }
        };

        return true;
    }

    /**
     * Start listening
     */
    start() {
        if (!this.recognition) {
            if (!this.init()) return false;
        }

        this.transcript = '';
        this.isListening = true;
        this.shouldContinue = true; // Enable auto-restart for mobile

        try {
            this.recognition.start();
            return true;
        } catch (e) {
            console.error('Error starting recognition:', e);
            this.shouldContinue = false;
            return false;
        }
    }

    /**
     * Stop listening
     */
    stop() {
        this.shouldContinue = false; // Disable auto-restart

        // Clear any pending restart timeout
        if (this.restartTimeout) {
            clearTimeout(this.restartTimeout);
            this.restartTimeout = null;
        }

        if (this.recognition && this.isListening) {
            this.recognition.stop();
            this.isListening = false;
        }
        return this.cleanTranscript(this.transcript);
    }

    /**
     * Clean transcript - remove filler words, repeated spaces, etc.
     */
    cleanTranscript(text) {
        if (!text) return '';

        return text
            // Remove common filler words in Spanish
            .replace(/\b(eh+|ehh+|ehhh+|mmm+|ummm+|umm+|este|pues nada|o sea|bueno pues|entonces pues)\b/gi, '')
            // Remove multiple spaces
            .replace(/\s+/g, ' ')
            // Remove spaces before punctuation
            .replace(/\s+([.,!?])/g, '$1')
            // Capitalize first letter
            .replace(/^\s*\w/, c => c.toUpperCase())
            // Trim
            .trim();
    }

    /**
     * Generate title from text (first 8 words)
     */
    generateTitle(text) {
        if (!text) return 'Idea sin título';

        const words = text.split(/\s+/).slice(0, 8);
        let title = words.join(' ');

        // Add ellipsis if truncated
        if (text.split(/\s+/).length > 8) {
            title += '...';
        }

        return title;
    }
}

export const speechService = new SpeechService();
export default speechService;
